import React, { useState } from 'react';
import { X, Plus, Edit2, Trash2, Check } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { addUserVideo, deleteUserVideo } from '../services/userVideoService';
import toast from 'react-hot-toast';

/**
 * ExerciseInfoModal (PR-06)
 *
 * Reusable modal for exercise details.
 * Used from Session (during workout) and Library (browsing).
 * Modal (not route) preserves workout state when dismissed.
 *
 * User Video Contribution:
 * Users can add YouTube video URLs to exercises without videos.
 * User-contributed videos are stored per-user in Firestore.
 */
function ExerciseInfoModal({ exercise, isOpen, onClose, onVideoUpdate }) {
  const { user } = useAuth();
  const [isEditingVideo, setIsEditingVideo] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  if (!isOpen || !exercise) return null;

  const hasUserVideo = exercise.media?.youtubeUrl && exercise.media?.userContributed;
  const hasAnyVideo = exercise.media?.youtubeUrl;

  const handleAddVideo = () => {
    setVideoUrl('');
    setIsEditingVideo(true);
  };

  const handleEditVideo = () => {
    setVideoUrl(exercise.media?.youtubeUrl || '');
    setIsEditingVideo(true);
  };

  const handleCancelEdit = () => {
    setIsEditingVideo(false);
    setVideoUrl('');
  };

  const handleSaveVideo = async () => {
    if (!user || !videoUrl.trim()) {
      toast.error('Please enter a YouTube URL');
      return;
    }

    setIsSaving(true);
    try {
      await addUserVideo(user.uid, exercise.id, videoUrl.trim());
      toast.success('Video added successfully');
      setIsEditingVideo(false);
      setVideoUrl('');
      if (onVideoUpdate) {
        onVideoUpdate(exercise.id, videoUrl.trim());
      }
    } catch (error) {
      console.error('Error adding video:', error);
      toast.error(error.message || 'Failed to add video');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteVideo = async () => {
    if (!user) return;

    const confirmed = window.confirm('Delete this video? This cannot be undone.');
    if (!confirmed) return;

    try {
      await deleteUserVideo(user.uid, exercise.id);
      toast.success('Video deleted');
      if (onVideoUpdate) {
        onVideoUpdate(exercise.id, null);
      }
    } catch (error) {
      console.error('Error deleting video:', error);
      toast.error('Failed to delete video');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-light-surface dark:bg-dark-surface w-full sm:max-w-2xl sm:rounded-[18px] rounded-t-[18px] max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-light-surface dark:bg-dark-surface border-b border-light-border dark:border-dark-border px-4 py-3 flex items-center justify-between">
          <h2 className="text-[20px] font-grotesk font-bold text-light-ink dark:text-dark-ink pr-8">
            {exercise.name}
          </h2>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-[12px] hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
          >
            <X size={20} className="text-light-ink dark:text-dark-ink" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Form Video (PR-05) + User Video Contribution */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[15px] font-plex font-semibold text-light-ink dark:text-dark-ink">
                Form Video
                {hasUserVideo && (
                  <span className="ml-2 text-[11px] font-medium text-light-accent dark:text-dark-accent uppercase tracking-wide">
                    User Added
                  </span>
                )}
              </h3>
              {user && !isEditingVideo && (
                <div className="flex gap-2">
                  {!hasAnyVideo && (
                    <button
                      onClick={handleAddVideo}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-plex font-medium text-light-accent dark:text-dark-accent hover:bg-light-accent-soft dark:hover:bg-dark-accent-soft rounded-[8px] transition-colors"
                    >
                      <Plus size={14} />
                      Add video
                    </button>
                  )}
                  {hasUserVideo && (
                    <>
                      <button
                        onClick={handleEditVideo}
                        className="p-1.5 text-light-muted dark:text-dark-muted hover:text-light-ink dark:hover:text-dark-ink rounded-[8px] hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={handleDeleteVideo}
                        className="p-1.5 text-light-muted dark:text-dark-muted hover:text-red-500 rounded-[8px] hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            {isEditingVideo ? (
              <div className="space-y-2">
                <input
                  type="url"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="w-full px-3 py-2.5 border border-light-border dark:border-dark-border rounded-[12px] bg-light-surface dark:bg-dark-surface text-light-ink dark:text-dark-ink font-plex text-[13px] focus:outline-none focus:ring-2 focus:ring-light-accent dark:focus:ring-dark-accent"
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveVideo}
                    disabled={isSaving}
                    className="flex-1 h-[44px] bg-light-accent dark:bg-dark-accent text-white rounded-[12px] font-plex font-semibold text-[13px] flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Check size={16} />
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    className="flex-1 h-[44px] border border-light-border dark:border-dark-border text-light-ink dark:text-dark-ink rounded-[12px] font-plex font-medium text-[13px] hover:bg-light-bg dark:hover:bg-dark-bg transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : hasAnyVideo ? (
              <div className="aspect-video rounded-[14px] overflow-hidden bg-black">
                <iframe
                  src={`https://www.youtube.com/embed/${exercise.media.youtubeUrl.split('v=')[1]?.split('&')[0]}`}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={`${exercise.name} form video`}
                />
              </div>
            ) : (
              <div className="aspect-video rounded-[14px] bg-light-bg dark:bg-dark-bg flex items-center justify-center border border-dashed border-light-border dark:border-dark-border">
                <p className="text-[13px] font-plex text-light-muted dark:text-dark-muted">
                  No video available
                </p>
              </div>
            )}
          </div>

          {/* Images */}
          {exercise.images && exercise.images.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-2">
              {exercise.images.map((img, i) => (
                <img
                  key={i}
                  src={img}
                  alt={`${exercise.name} ${i + 1}`}
                  className="h-48 rounded-[14px] object-cover flex-shrink-0"
                />
              ))}
            </div>
          )}

          {/* Target Muscles */}
          {(exercise.primaryMuscles?.length > 0 || exercise.secondaryMuscles?.length > 0) && (
            <div>
              <h3 className="text-[15px] font-plex font-semibold text-light-ink dark:text-dark-ink mb-2">
                Target Muscles
              </h3>
              <div className="space-y-1">
                {exercise.primaryMuscles && exercise.primaryMuscles.length > 0 && (
                  <div className="text-[13px] font-plex text-light-muted dark:text-dark-muted">
                    <span className="font-medium text-light-ink dark:text-dark-ink">Primary: </span>
                    {exercise.primaryMuscles.join(', ')}
                  </div>
                )}
                {exercise.secondaryMuscles && exercise.secondaryMuscles.length > 0 && (
                  <div className="text-[13px] font-plex text-light-muted dark:text-dark-muted">
                    <span className="font-medium text-light-ink dark:text-dark-ink">Secondary: </span>
                    {exercise.secondaryMuscles.join(', ')}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Description */}
          {exercise.description && (
            <div>
              <h3 className="text-[15px] font-plex font-semibold text-light-ink dark:text-dark-ink mb-2">
                Description
              </h3>
              <p className="text-[13px] font-plex text-light-muted dark:text-dark-muted leading-relaxed">
                {exercise.description}
              </p>
            </div>
          )}

          {/* Cues */}
          {exercise.cues && exercise.cues.length > 0 && (
            <div>
              <h3 className="text-[15px] font-plex font-semibold text-light-ink dark:text-dark-ink mb-2">
                Form Cues
              </h3>
              <ul className="space-y-1.5">
                {exercise.cues.map((cue, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-light-accent dark:text-dark-accent font-plex text-[13px] font-medium">•</span>
                    <span className="text-[13px] font-plex text-light-muted dark:text-dark-muted leading-relaxed">
                      {cue}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Common Mistakes */}
          {exercise.commonMistakes && exercise.commonMistakes.length > 0 && (
            <div>
              <h3 className="text-[15px] font-plex font-semibold text-light-ink dark:text-dark-ink mb-2">
                Common Mistakes
              </h3>
              <ul className="space-y-1.5">
                {exercise.commonMistakes.map((mistake, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-light-accent dark:text-dark-accent font-plex text-[13px] font-medium">×</span>
                    <span className="text-[13px] font-plex text-light-muted dark:text-dark-muted leading-relaxed">
                      {mistake}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Technical Details */}
          <div>
            <h3 className="text-[15px] font-plex font-semibold text-light-ink dark:text-dark-ink mb-2">
              Technical Details
            </h3>
            <div className="space-y-1">
              {exercise.movementPattern && (
                <div className="text-[13px] font-plex text-light-muted dark:text-dark-muted">
                  <span className="font-medium text-light-ink dark:text-dark-ink">Movement: </span>
                  {exercise.movementPattern.replace('-', ' ')}
                </div>
              )}
              {exercise.mechanic && (
                <div className="text-[13px] font-plex text-light-muted dark:text-dark-muted">
                  <span className="font-medium text-light-ink dark:text-dark-ink">Mechanic: </span>
                  {exercise.mechanic}
                </div>
              )}
              <div className="text-[13px] font-plex text-light-muted dark:text-dark-muted">
                <span className="font-medium text-light-ink dark:text-dark-ink">Equipment: </span>
                {exercise.equipment && exercise.equipment.length > 0 ? (
                  exercise.equipment.map(e => e.replace('_', ' ')).join(', ')
                ) : (
                  <span className="italic">Bodyweight</span>
                )}
              </div>
              {(exercise.defaultSets || exercise.defaultReps) && (
                <div className="text-[13px] font-plex text-light-muted dark:text-dark-muted">
                  <span className="font-medium text-light-ink dark:text-dark-ink">Default: </span>
                  {exercise.defaultSets} × {exercise.defaultReps}
                  {exercise.defaultRestSeconds && ` · ${exercise.defaultRestSeconds}s rest`}
                </div>
              )}
              {exercise.unilateral && (
                <div className="text-[13px] font-plex text-light-muted dark:text-dark-muted">
                  <span className="font-medium text-light-ink dark:text-dark-ink">Unilateral: </span>
                  Yes (per side)
                </div>
              )}
            </div>
          </div>

          {/* Placeholder for PR-05: YouTube video link */}
          {/* Will add video integration in PR-05 */}
        </div>

        {/* Close button at bottom for mobile */}
        <div className="sticky bottom-0 bg-light-surface dark:bg-dark-surface border-t border-light-border dark:border-dark-border p-4 sm:hidden">
          <button
            onClick={onClose}
            className="w-full h-[52px] bg-light-accent dark:bg-dark-accent text-white rounded-[14px] font-plex font-semibold text-[15px]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default ExerciseInfoModal;
